import React, { useEffect, useMemo, useState } from 'react';
import Tooltip from '../components/tooltip';
import Acordion from '../components/Acordion';
import AcordionItem from '../components/AcordionItem';
import Modal from '../components/Modal';
import ResumenCalculo from '../components/ResumenCalculo';
import Loader from '../components/Loader';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5257';

const CalculoAlmacenaje = ({ onCalculoTerminado }) => {
  const [puertos, setPuertos] = useState([]);
  const [puertoId, setPuertoId] = useState('');
  const [servicios, setServicios] = useState([]);
  const [servicioKey, setServicioKey] = useState('');

  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  const [tipoOperacion, setTipoOperacion] = useState('');

  const [unidades, setUnidades] = useState('1');

  // Estado para el cálculo detallado
  const [tipoContenedor, setTipoContenedor] = useState('20_lleno');
  const [cantidad, setCantidad] = useState('1');

  const [modalData, setModalData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cargando, setCargando] = useState(false);

  const [mostrarAviso, setMostrarAviso] = useState(false);

  const toIdString = (id) => {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (id.$oid) return id.$oid;
    try { return String(id); } catch { return ''; }
  };
  const safeLower = (v) => (typeof v === 'string' ? v.toLowerCase() : '');
  const sinAcentos = (s) =>
    (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  const etiquetaServicio = (s) => {
    const nombre = s.servicioNombre || s.servicio || 'Servicio';
    const tipo = s.tipo || '';
    const trafico = s.tipo_de_trafico || s.tipoDeTrafico || '';
    const base = [nombre, tipo].filter(Boolean).join(' · ');
    return trafico ? `${base} (${trafico})` : base;
  };

  const svcKey = (s) => [
    (s.servicioNombre ?? s.servicio ?? '').trim().toLowerCase(),
    (s.tipo ?? '').trim().toLowerCase(),
    (s.tipo_de_trafico ?? s.tipoDeTrafico ?? '').trim().toLowerCase()
  ].join('|');

  // 1. Cargar Puertos
  useEffect(() => {
    setCargando(true);
    fetch(`${API_BASE_URL}/api/Puerto`)
      .then(r => r.json())
      .then(list => {
        const norm = (list || []).map(p => ({
          ...p,
          _id: toIdString(p._id),
          id: toIdString(p.id),
        })).sort((a,b)=> (a.nombre||'').localeCompare(b.nombre||''));
        setPuertos(norm);
      })
      .catch(()=> setPuertos([]))
      .finally(()=> setCargando(false));
  }, []);

  // 2. Cargar Servicios por Puerto
  useEffect(() => {
    setServicios([]);
    setServicioKey('');
    setTipoOperacion('');
    if (!puertoId) return;

    setCargando(true);
    fetch(`${API_BASE_URL}/api/Servicio/por-puerto/${encodeURIComponent(puertoId)}`)
      .then(r => r.json())
      .then(list => {
        const soloAlm = (list || []).filter(s =>
          safeLower(s.servicioNombre || s.servicio || '').includes('almacen')
        );

        const map = new Map();
        for (const raw of soloAlm) {
          const s = { ...raw, _id: toIdString(raw.id || raw._id) };
          const key = svcKey(s);
          if (!map.has(key)) map.set(key, { ...s, __key: key });
        }
        const arr = [...map.values()].sort((a,b)=> etiquetaServicio(a).localeCompare(etiquetaServicio(b)));
        setServicios(arr);
      })
      .catch(()=> setServicios([]))
      .finally(()=> setCargando(false));
  }, [puertoId]);


  const puertoSel = useMemo(
    () => puertos.find(p => (p.id || p._id) === puertoId),
    [puertos, puertoId]
  );

  const servicioSel = useMemo(
    () => servicios.find(s => s.__key === servicioKey),
    [servicios, servicioKey]
  );

  const esContenerizada = useMemo(() => {
    const s = servicioSel;
    if (!s) return false;

    const tieneTarifasDetalle = 
        (s.tarifa_20_pies_lleno > 0) || (s.tarifa20PiesLleno > 0) ||
        (s.tarifa_40_pies_lleno > 0) || (s.tarifa40PiesLleno > 0);
        
    if (tieneTarifasDetalle) return true;

    if (safeLower(s.tipo) === 'carga contenerizada') return true;
    
    const texto = [
      s.servicioNombre, s.servicio, s.tipo, s.categoria, s.subcategoria
    ].map(safeLower).join(' ');

    return (
      texto.includes('conteneriz') ||
      texto.includes('carga contenerizada') ||
      texto.includes('contenedor')
    );
  }, [servicioSel]);

  const esSalinaCruz = useMemo(() => {
    if (!puertoSel) return false;
    const n = sinAcentos(puertoSel.nombre);
    return n.includes('salina cruz');
  }, [puertoSel]);

  const mostrarContenedorUI = esContenerizada;

  const textoServicioSeleccionado = useMemo(() => {
    if (!servicioSel) return '';
    const nombre = servicioSel.servicioNombre || servicioSel.servicio || 'almacenaje';
    const tipo = servicioSel.tipo || 'carga contenerizada';
    const trafico = servicioSel.tipo_de_trafico || servicioSel.tipoDeTrafico || '';
    const base = [nombre, tipo].filter(Boolean).join(' · ');
    return trafico ? `${base} (${trafico})` : base;
  }, [servicioSel]);

  const modalidad = mostrarContenedorUI ? 'contenedor' : (servicioSel ? 'por_dia' : '');

  useEffect(() => {
    if (esSalinaCruz && mostrarContenedorUI) setMostrarAviso(true);
    else setMostrarAviso(false);
  }, [mostrarContenedorUI, esSalinaCruz]);

  const validarFechas = () => {
    if (!fechaInicio || !fechaFin) return 'Indica fecha inicio y fecha fin.';
    if (new Date(fechaFin) < new Date(fechaInicio)) return 'La fecha fin no puede ser menor a la fecha inicio.';
    return '';
  };

  async function calcular() {
    setModalData(null);
    setIsModalOpen(false);

    if (!puertoId || !servicioSel) {
      setModalData({ error: 'Selecciona puerto y servicio.' });
      setIsModalOpen(true);
      return;
    }

    if (!esSalinaCruz && !tipoOperacion) {
      setModalData({ error: 'Selecciona si es Importación o Exportación.' });
      setIsModalOpen(true);
      return;
    }
    const err = validarFechas();
    if (err) {
      setModalData({ error: err });
      setIsModalOpen(true);
      return;
    }

    const servicioId = servicioSel._id;
    const nombreServicio = etiquetaServicio(servicioSel);

    const base = {
      puertoId,
      servicioId,
      fechaInicio: fechaInicio || null,
      fechaFin: fechaFin || null,
      tipoOperacion: esSalinaCruz ? (tipoOperacion || null) : tipoOperacion 
    };

    let cantidadNum = 1;
    if (mostrarContenedorUI) {
      cantidadNum = Number(cantidad) || 1;
    } else {
      cantidadNum = Number(unidades) || 1;
    }

    const body = mostrarContenedorUI
      ? { ...base, modalidad: 'contenedor', tipoContenedor, cantidad: cantidadNum }
      : { ...base, modalidad: 'por_dia', unidades: cantidadNum };

    try {
      setCargando(true);
      const res = await fetch(`${API_BASE_URL}/api/calculos/almacenaje`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        let msg = 'Error al calcular.';
        try { const e = await res.json(); if (e?.error) msg = e.error; } catch {}
        throw new Error(msg);
      }
      const data = await res.json();
      
      // MODIFICACIÓN: Enviar total al padre
      if (onCalculoTerminado && typeof onCalculoTerminado === 'function') {
        onCalculoTerminado(data.total);
      }

      let criterioStr = '';
      if (mostrarContenedorUI) {
        const tipoLabel = tipoContenedor
            .replace('20_', "20' ")
            .replace('40_', "40' ")
            .replace('lleno', 'Lleno')
            .replace('vacio', 'Vacío');
            
        criterioStr = `${cantidad} x ${tipoLabel}`;
      } else {
        const u = servicioSel?.unidadCobro || servicioSel?.unidad_cobro || servicioSel?.UnidadCobro || 'unidades';
        criterioStr = `${unidades} ${u}`;
      }
      if (tipoOperacion) {
        criterioStr += ` (${tipoOperacion})`;
      }

      const modalPayload = {
        total: data.total,
        servicio: nombreServicio, 
        periodoInicio: fechaInicio,
        periodoFin: fechaFin,
        criterio: criterioStr,
        desglose: data.detalle,
        diasCobrables: data.diasCobrables,
        tramos_tarifa: data.tramos_tarifa,
        cantidadAplicada: cantidadNum 
      };

      setModalData(modalPayload);
      setIsModalOpen(true);

    } catch (e) {
      setModalData({ error: e.message || 'Error al calcular.' });
      setIsModalOpen(true);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    setModalData(null);
    setIsModalOpen(false);
    if (mostrarContenedorUI) {
      setTipoContenedor('20_lleno');
      setCantidad('1');
    } else {
      setUnidades('1');
    }
  }, [servicioKey, mostrarContenedorUI]);


  return (
    <>
      <Acordion defaultOpenIndex={0}>
        <AcordionItem title="Almacenaje">
          {cargando ? (
            <Loader message="Procesando solicitud..." />
          ) : (
            <div style={{ maxWidth:'600px', margin:'0 auto' }}>
              <label>Puerto</label>
              <select
                value={puertoId}
                onChange={e => setPuertoId(String(e.target.value))}
                className="form-control"
                disabled={cargando}
              >
                <option value="" disabled>Selecciona un puerto</option>
                {puertos.map(p => {
                  const value = p.id || p._id;
                  return (
                    <option key={value} value={value}>{p.nombre}</option>
                  );
                })}
              </select>

              <div style={{ display: "flex", alignItems: "center", marginBottom: ".25rem" }}>
                <label style={{ flex: 1 }}>Selecciona servicio de almacenaje</label>
                <Tooltip text="Cabotaje es el transporte marítimo de mercancías o pasajeros entre puertos del mismo país." />
              </div>
              <select
                value={servicioKey}
                onChange={e => setServicioKey(e.target.value)}
                className="form-control"
                disabled={!puertoId || servicios.length === 0 || cargando}
              >
                <option value="" disabled>
                  {puertoId ? 'Selecciona servicio de almacenaje' : 'Primero el puerto'}
                </option>
                {servicios.map(s => (
                  <option key={s.__key} value={s.__key} title={etiquetaServicio(s)}>
                    {etiquetaServicio(s)}
                  </option>
                ))}
              </select>

              {mostrarAviso && (
                <div className="aviso-box">
                  <div className="aviso-header">
                    <span>Condiciones para contenedores en Salina Cruz</span>
                    <button
                      type="button"
                      className="aviso-close"
                      aria-label="Cerrar aviso"
                      onClick={() => setMostrarAviso(false)}
                    >
                      ×
                    </button>
                  </div>
                  <ul className="aviso-list">
                    <li>Importes en pesos en moneda nacional.</li>
                    <li>Cuotas más el Impuesto al Valor Agregado.</li>
                    <li>
                      Cajas sobredimensionadas que ocupen un área mayor a la de un contenedor estándar
                      o que no permitan estiba superior tienen un recargo del 100%.
                    </li>
                    <li>Plazo libre de almacenaje de <b>15 días</b> para contenedores de comercio exterior.</li>
                    <li>Plazo libre de <b>45 días</b> para contenedores en tráfico de cabotaje.</li>
                  </ul>
                </div>
              )}

              {!esSalinaCruz && (
                <>
                  <label>Tipo de operación</label>
                  <select
                    value={tipoOperacion}
                    onChange={e => setTipoOperacion(e.target.value)}
                    className="form-control"
                    disabled={cargando}
                  >
                    <option value="" disabled>Selecciona: importación o exportación</option>
                    <option value="importacion">Importación</option>
                    <option value="exportacion">Exportación</option>
                  </select>
                </>
              )}

              <label>Fecha inicio</label>
              <input
                type="date"
                value={fechaInicio}
                onChange={e => setFechaInicio(e.target.value)}
                className="form-control"
                disabled={cargando}
              />

              <label>Fecha fin</label>
              <input
                type="date"
                value={fechaFin}
                onChange={e => setFechaFin(e.target.value)}
                className="form-control"
                disabled={cargando}
              />

              {mostrarContenedorUI && (
                <div style={{ border:'1px solid #ccc', padding:'1rem', marginBottom:'.75rem', borderRadius:'8px', backgroundColor: '#f9f9f9' }}>
                  <strong style={{ display:'block', marginBottom:'.25rem' }}>
                    {textoServicioSeleccionado}
                  </strong>
                  <small style={{ display:'block', marginBottom:'.75rem', color:'#555' }}>
                    Cálculo Detallado por Tipo
                  </small>

                  <label>Tipo de contenedor</label>
                  <select
                    value={tipoContenedor}
                    onChange={e => setTipoContenedor(e.target.value)}
                    className="form-control"
                    disabled={cargando}
                  >
                    <option value="20_lleno">20' Lleno</option>
                    <option value="20_vacio">20' Vacío</option>
                    <option value="40_lleno">40' Lleno</option>
                    <option value="40_vacio">40' Vacío</option>
                  </select>

                  <label>Cantidad</label>
                  <input
                    type="number"
                    min="1"
                    value={cantidad}
                    onChange={e => setCantidad(e.target.value)}
                    className="form-control"
                    disabled={cargando}
                  />
                </div>
              )}

              {!mostrarContenedorUI && servicioSel && (
                <>
                  <label>
                    {servicioSel.unidadCobro || servicioSel.unidad_cobro || servicioSel.UnidadCobro || 'Unidades'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={unidades}
                    onChange={e => setUnidades(e.target.value)}
                    className="form-control"
                    disabled={cargando}
                  />
                </>
              )}

              <button
                onClick={calcular}
                className="form-control"
                disabled={
                  cargando ||
                  !puertoId ||
                  !servicioKey ||
                  (!esSalinaCruz && !tipoOperacion)
                }
              >
                {cargando ? 'Calculando…' : 'Calcular'}
              </button>

              {modalidad && (
                <p style={{ marginTop:'.5rem', fontSize:'.9rem', color:'#555' }}>
                  Modalidad detectada: <b>{modalidad === 'contenedor' ? 'Carga Contenerizada (Detallada)' : 'Por Días/Tramos'}</b>
                </p>
              )}
            </div>
          )}
        </AcordionItem>
      </Acordion>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        {modalData && (
          modalData.error ? (
            <div>
              <h2 style={{ marginTop: 0, color: '#D32F2F' }}>Error en el Cálculo</h2>
              <p>{modalData.error}</p>
            </div>
          ) : (
            <ResumenCalculo data={modalData} />
          )
        )}
      </Modal>
    </>
  );
};

export default CalculoAlmacenaje;